import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSession } from "@/lib/session";

type MappedUser = {
  id: string;
  name: string;
  mobile?: string;
  email?: string;
  role: string;
  isActive?: boolean;
  status?: string;
};

type PbxExt = {
  number: string;
  username?: string;
  status?: string;
  type?: string;
  mapped: MappedUser[];
};

type UserRow = {
  id: number | string;
  name: string;
  mobile?: string;
  extension: string | null;
  role: string;
  status: string;
};

function presenceClass(status?: string) {
  const p = String(status || "");
  if (/inuse|talk|busy|answer/i.test(p)) return "live";
  if (/ring/i.test(p)) return "ring";
  if (/unavail|missing|fail|unknown|disconnect|offline/i.test(p)) return "dead";
  if (!p) return "dead";
  return "idle";
}

function lineLabel(type?: string) {
  const t = String(type || "sip").toUpperCase();
  if (/FXS/.test(t)) return "FXS";
  if (/FXO/.test(t)) return "FXO";
  if (/GSM|SIM|LTE/.test(t)) return "SIM";
  return "SIP";
}

export default function ExtensionsPage() {
  const { user } = useSession();
  const canEdit = ["owner", "admin"].includes(String(user?.role || ""));
  const [rows, setRows] = useState<PbxExt[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);
  const [assigning, setAssigning] = useState<PbxExt | null>(null);

  async function reload() {
    const [exts, people] = await Promise.all([
      api<PbxExt[]>("/org/extensions"),
      api<UserRow[]>("/users").catch(() => [] as UserRow[]),
    ]);
    setRows(Array.isArray(exts) ? exts : []);
    setUsers(Array.isArray(people) ? people : []);
    return Array.isArray(exts) ? exts : [];
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await reload();
        if (cancelled) return;
        if (list.length === 0 && canEdit) {
          setBusy(true);
          try {
            const synced = await api<PbxExt[]>("/org/extensions/sync", { method: "POST" });
            if (!cancelled && Array.isArray(synced)) setRows(synced);
            else if (!cancelled) await reload();
            if (!cancelled) setOk("Synced from PBX.");
          } catch (e) {
            if (!cancelled) {
              setError(
                e instanceof Error
                  ? e.message
                  : "Could not sync from PBX. Check MQTT / PBX.",
              );
            }
          } finally {
            if (!cancelled) setBusy(false);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Could not load extensions");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canEdit]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...rows].sort((a, b) =>
      a.number.localeCompare(b.number, undefined, { numeric: true }),
    );
    if (!q) return list;
    return list.filter(
      (e) =>
        e.number.includes(q) ||
        (e.type || "").toLowerCase().includes(q) ||
        (e.status || "").toLowerCase().includes(q) ||
        e.mapped.some(
          (u) =>
            u.name.toLowerCase().includes(q) ||
            String(u.mobile || u.email || "").includes(q),
        ),
    );
  }, [rows, query]);

  const mappedCount = rows.filter((e) => e.mapped.length > 0).length;

  const mapCandidates = useMemo(() => {
    if (!assigning) return [];
    // One extension → one user: only unmapped users, or the user already on this desk.
    return users.filter(
      (u) => !u.extension || String(u.extension) === String(assigning.number),
    );
  }, [users, assigning]);

  async function syncFromPbx() {
    setBusy(true);
    setError("");
    setOk("");
    try {
      const res = await api<PbxExt[]>("/org/extensions/sync", { method: "POST" });
      if (Array.isArray(res)) setRows(res);
      else await reload();
      setOk("Synced from PBX.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not sync from PBX");
      try {
        await reload();
      } catch {
        /* keep prior */
      }
    } finally {
      setBusy(false);
    }
  }

  async function assign(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!assigning) return;
    const form = new FormData(e.currentTarget);
    const userId = String(form.get("userId") || "");
    if (!userId) return;
    setBusy(true);
    setError("");
    setOk("");
    try {
      await api(`/users/${userId}`, {
        method: "PUT",
        body: JSON.stringify({ extension: assigning.number }),
      });
      setAssigning(null);
      await reload();
      setOk(`Mapped to ${assigning.number}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not map user");
    } finally {
      setBusy(false);
    }
  }

  async function unmap(userId: string, extension: string) {
    if (!window.confirm(`Unmap user from ${extension}?`)) return;
    setBusy(true);
    setError("");
    setOk("");
    try {
      await api(`/users/${userId}`, {
        method: "PUT",
        body: JSON.stringify({ extension: "" }),
      });
      await reload();
      setOk(`Unmapped from ${extension}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not unmap");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ext-page">
      <div className="ext-page-head">
        <div>
          <h1>Extensions</h1>
          <p className="muted">
            {rows.length} on PBX · {mappedCount} mapped ·{" "}
            <Link to="/app/users">Users</Link>
          </p>
        </div>
        <div className="ext-page-tools">
          <input
            className="search"
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn ghost" type="button" disabled={busy} onClick={() => void reload()}>
            Refresh
          </button>
          {canEdit && (
            <button className="btn" type="button" disabled={busy} onClick={() => void syncFromPbx()}>
              {busy ? "Syncing…" : "Sync from PBX"}
            </button>
          )}
        </div>
      </div>

      {error ? <p className="error ext-flash">{error}</p> : null}
      {ok ? <p className="ok-msg ext-flash">{ok}</p> : null}

      <section className="panel ext-panel">
        <table className="table ext-table">
          <thead>
            <tr>
              <th>Ext</th>
              <th>Type</th>
              <th>Status</th>
              <th>Mapped user</th>
              {canEdit ? <th className="ext-col-actions">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 5 : 4} className="muted">
                  {rows.length === 0
                    ? "No extensions yet. Sync from PBX (MQTT must be connected)."
                    : "No matches."}
                </td>
              </tr>
            )}
            {visible.map((e) => (
              <tr key={e.number}>
                <td className="ext-read">{e.number}</td>
                <td>
                  <span className="pill">{lineLabel(e.type)}</span>
                </td>
                <td>
                  <span className={`pill ${presenceClass(e.status)}`}>
                    {e.status || "unknown"}
                  </span>
                </td>
                <td>
                  {e.mapped.length === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    <ul className="ext-user-list">
                      {e.mapped.map((u) => (
                        <li key={u.id}>
                          <div className="ext-user-main">
                            <b>{u.name}</b>
                            <span className="muted">
                              {[u.role, u.mobile || u.email].filter(Boolean).join(" · ")}
                            </span>
                          </div>
                          {canEdit ? (
                            <button
                              type="button"
                              className="btn ghost btn-tiny"
                              disabled={busy}
                              onClick={() => void unmap(u.id, e.number)}
                            >
                              Unmap
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                {canEdit ? (
                  <td className="ext-col-actions">
                    <button
                      className="btn ghost btn-tiny"
                      type="button"
                      disabled={busy}
                      onClick={() => setAssigning(e)}
                    >
                      Map
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {assigning && (
        <div className="modal-back" onClick={() => setAssigning(null)}>
          <div className="modal user-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h2>Map user · {assigning.number}</h2>
                <p className="muted">
                  One extension links to one user only.
                  {assigning.mapped[0]
                    ? ` Currently: ${assigning.mapped[0].name}.`
                    : " Pick an unmapped CRM user."}
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={() => setAssigning(null)}
              >
                ×
              </button>
            </div>
            <form className="modal-form" onSubmit={(ev) => void assign(ev)}>
              <div className="modal-body">
                <label className="field">
                  <span>User</span>
                  <select name="userId" required defaultValue={assigning.mapped[0]?.id || ""}>
                    <option value="" disabled>
                      Select unmapped user
                    </option>
                    {mapCandidates.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                        {u.mobile ? ` · ${u.mobile}` : ""}
                        {String(u.extension) === String(assigning.number) ? " (current)" : ""}
                      </option>
                    ))}
                  </select>
                  <span className="field-hint">
                    {mapCandidates.length === 0
                      ? "All users already have an extension. Unmap one first, or add a new user."
                      : "Users already mapped to another desk are hidden."}
                  </span>
                </label>
                {error ? <p className="error">{error}</p> : null}
              </div>
              <div className="btn-row">
                <button className="btn" disabled={busy || mapCandidates.length === 0}>
                  {busy ? "Saving…" : assigning.mapped[0] ? "Replace mapping" : "Save"}
                </button>
                <button className="btn ghost" type="button" onClick={() => setAssigning(null)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
